/* ============================================================
 * src/mock/circa.ts — TEMPORARY mock store.
 *
 * Faithful ESM transcription of the v.3 prototype's data.js (which
 * exposed window.CIRCA in the browser-Babel demo). The CODE-phase
 * wiring tasks (T304+) replace each screen's usage of `CIRCA.*`
 * with the real Convex subscription, screen-by-screen; this shim
 * stays until the last screen is wired, at which point T350 will
 * audit-and-delete it.
 *
 * Keep the surface 1:1 with the prototype: any helper the screens
 * import must exist here with the same name and shape.
 * ============================================================ */

type Member = {
  id: string;
  name: string;
  firstName: string;
  email: string;
  role: 'admin' | 'editor' | 'viewer';
  initials: string;
  timezone: string;
};

const rnd = (seed => () => (seed = (seed * 9301 + 49297) % 233280) / 233280)(7);
const pick = <T>(arr: T[]): T => arr[Math.floor(rnd() * arr.length)];
const range = (n: number): number[] => Array.from({ length: n }, (_, i) => i);

function series(n: number, base: number, variance: number): number[] {
  let v = base;
  return range(n).map(() => {
    v = Math.max(0, v + (rnd() - 0.5) * variance);
    return Math.round(v);
  });
}

const member: Member = {
  id: 'm_001', name: 'Lucía Ferrán', firstName: 'Lucía',
  email: 'lucia@canalcirca.tv', role: 'admin', initials: 'LF',
  timezone: 'America/Mexico_City',
};

const PERMS: Record<string, Set<string>> = {
  admin: new Set(['asset_create','asset_update','asset_delete','asset_tag','schedule_create','schedule_publish','template_manage','channel_create','channel_archive','channel_transfer','channel_mode_set','streamkey_reveal','streamkey_rotate','streamkey_revoke','edge_node_lifecycle','provisioning_run','member_invite','member_remove','member_role_change','podcast_publish','media_transform_invoke','audio_retry','read']),
  editor: new Set(['asset_create','asset_update','asset_delete','asset_tag','schedule_create','schedule_publish','template_manage','channel_create','podcast_publish','media_transform_invoke','audio_retry','read']),
  viewer: new Set(['read']),
};
function can(perm: string): boolean {
  return Boolean(PERMS[member.role] && PERMS[member.role].has(perm));
}

const channelNames: Array<[string, string, string]> = [
  ['Noticias 24', 'noticias-24', 'Cobertura informativa continua, todo el día.'],
  ['Ritmo Latino', 'ritmo-latino', 'Música y videoclips sin parar.'],
  ['Deportes Vivo', 'deportes-vivo', 'Resúmenes y eventos deportivos en directo.'],
  ['Cine Clásico', 'cine-clasico', 'Películas de catálogo, programadas por bloques.'],
  ['Documental', 'documental', 'Naturaleza, ciencia y reportajes de larga duración.'],
  ['Aprende+', 'aprende-mas', 'Contenido educativo para todas las edades.'],
  ['Cocina Abierta', 'cocina-abierta', 'Recetas y programas gastronómicos.'],
  ['Kids Mundo', 'kids-mundo', 'Animación y contenido infantil supervisado.'],
];
const providers = ['AWS', 'Hetzner', 'Gcore', 'Cloudflare'];
const channels = channelNames.map((c, i) => {
  const status = i === 0 ? 'live' : i === 1 ? 'live' : i === 2 ? 'adbreak' : i === 3 ? 'idle' : i === 4 ? 'live' : i === 5 ? 'error' : i === 6 ? 'idle' : 'starting';
  return {
    id: 'ch_' + (i + 1), name: c[0], slug: c[1], description: c[2],
    status, provider: providers[i % providers.length],
    destination: pick(['YouTube', 'O&O', 'Both']),
    timezone: 'America/Mexico_City',
    playbackMode: i % 2 ? 'linear' : 'loop',
    loopEnabled: i % 2 === 0,
    primaryColor: ['#003c33','#1863dc','#ff7759','#1e5d3f','#075e54','#9b60aa','#b45309','#0e7490'][i],
    uptime: (99 - rnd() * 1.6).toFixed(2),
    quality: Math.round(78 + rnd() * 21),
    viewers: status === 'live' || status === 'adbreak' ? Math.round(200 + rnd() * 9000) : 0,
    nowPlaying: ['El mundo hoy — edición tarde','Top 20 de la semana','Liga — jornada 14','Casablanca (1942)','Planeta océano — ep. 3','Álgebra desde cero','Pastas frescas','La granja musical'][i],
    spark: series(24, 50, 30),
  };
});

const assetTitles = [
  'El mundo hoy — edición tarde','Top 20 de la semana','Liga — jornada 14 resumen',
  'Casablanca (1942)','Planeta océano — episodio 3','Álgebra desde cero — módulo 1',
  'Entrevista exclusiva: ministra de cultura','Bumper — identidad de marca 10s',
  'Anuncio casa — patrocinador café','Documental: ciudades del futuro',
  'Receta: paella valenciana','Concierto en vivo — sinfónica nacional',
  'Noticiero matutino — bloque A','Cortometraje: el último tren','Tutorial: edición de video',
  'Relleno — paisajes 5min','Promo temporada nueva','Cápsula: salud y bienestar',
];
const assetStatuses = ['ready','ready','ready','processing','ready','ready','ready','ready','ready','processing','ready','ready','ready','uploading','ready','ready','ready','ready'];
const assetTypes = ['VOD','VOD','VOD','VOD','VOD','VOD','VOD','Bumper','House Ad','VOD','VOD','VOD','VOD','VOD','VOD','Filler','House Ad','VOD'];
const assets = assetTitles.map((t, i) => ({
  id: 'as_' + (i + 1), title: t, status: assetStatuses[i], type: assetTypes[i],
  durationMs: (assetTypes[i] === 'Bumper' ? 10 : assetTypes[i] === 'House Ad' ? 30 : Math.round(120 + rnd() * 6000)) * 1000,
  resolution: pick(['1920×1080','1280×720','3840×2160']),
  tags: pick([['noticias','actualidad'],['música'],['deporte'],['cine','clásico'],['naturaleza'],['educación'],['entrevista']]),
  addedAt: Date.now() - i * 3600_000 * 7,
  markers: Math.round(rnd() * 5),
  progress: assetStatuses[i] === 'processing' ? Math.round(rnd() * 90) : assetStatuses[i] === 'uploading' ? Math.round(rnd() * 60) : 100,
}));

const schedules = [
  { id: 'sc_1', name: 'Parrilla diaria — Noticias 24', status: 'live', channel: 'Noticias 24', items: 42, durationH: 24, gaps: 0, breaks: 18, modified: Date.now() - 3600_000 },
  { id: 'sc_2', name: 'Bloque musical nocturno', status: 'ready', channel: 'Ritmo Latino', items: 28, durationH: 8, gaps: 1, breaks: 6, modified: Date.now() - 3600_000 * 5 },
  { id: 'sc_3', name: 'Fin de semana deportivo', status: 'scheduled', channel: 'Deportes Vivo', items: 16, durationH: 12, gaps: 0, breaks: 9, modified: Date.now() - 3600_000 * 26 },
  { id: 'sc_4', name: 'Ciclo de cine clásico', status: 'draft', channel: 'Cine Clásico', items: 8, durationH: 16, gaps: 2, breaks: 4, modified: Date.now() - 3600_000 * 50 },
  { id: 'sc_5', name: 'Maratón documental', status: 'completed', channel: 'Documental', items: 12, durationH: 18, gaps: 0, breaks: 5, modified: Date.now() - 3600_000 * 120 },
  { id: 'sc_6', name: 'Programación educativa', status: 'draft', channel: 'Aprende+', items: 20, durationH: 10, gaps: 0, breaks: 8, modified: Date.now() - 3600_000 * 200 },
];

const alerts = [
  { id: 'al_1', sev: 'critical', type: 'youtube_stream_offline', channel: 'Aprende+', text: 'La señal del canal se interrumpió en el destino de publicación.', friendly: 'Transmisión fuera de línea', ts: Date.now() - 1000 * 60 * 4, resolved: false, read: false },
  { id: 'al_2', sev: 'warning', type: 'heartbeat_gap', channel: 'Deportes Vivo', text: 'Se detectó una interrupción breve en la señal.', friendly: 'Interrupción de señal', ts: Date.now() - 1000 * 60 * 22, resolved: false, read: false },
  { id: 'al_3', sev: 'warning', type: 'cost_threshold_exceeded', channel: null, text: 'El gasto del mes superó el 80% del umbral configurado.', friendly: 'Umbral de costo', ts: Date.now() - 1000 * 60 * 90, resolved: false, read: true },
  { id: 'al_4', sev: 'info', type: 'slo_breached', channel: 'Ritmo Latino', text: 'El tiempo activo del canal bajó del 99,9% en las últimas 24 h.', friendly: 'Objetivo de disponibilidad', ts: Date.now() - 1000 * 60 * 200, resolved: false, read: true },
  { id: 'al_5', sev: 'info', type: 'asset_ready', channel: null, text: 'El video "Documental: ciudades del futuro" terminó de procesarse.', friendly: 'Contenido listo', ts: Date.now() - 1000 * 60 * 320, resolved: true, read: true },
];

const members = [
  { id: 'm_001', name: 'Lucía Ferrán', email: 'lucia@canalcirca.tv', role: 'admin', status: 'active', initials: 'LF', lastSeen: Date.now() - 1000 * 60 * 2 },
  { id: 'm_002', name: 'Diego Salcedo', email: 'diego@canalcirca.tv', role: 'editor', status: 'active', initials: 'DS', lastSeen: Date.now() - 1000 * 60 * 40 },
  { id: 'm_003', name: 'María Quintana', email: 'maria@canalcirca.tv', role: 'editor', status: 'active', initials: 'MQ', lastSeen: Date.now() - 1000 * 60 * 60 * 3 },
  { id: 'm_004', name: 'Tomás Reyes', email: 'tomas@canalcirca.tv', role: 'viewer', status: 'invited', initials: 'TR', lastSeen: null },
  { id: 'm_005', name: 'Ana Gil', email: 'ana@externo.com', role: 'viewer', status: 'suspended', initials: 'AG', lastSeen: Date.now() - 1000 * 60 * 60 * 24 * 9 },
];

const providerSpend = [
  { slug: 'gcore', label: 'Entrega de borde (Gcore)', mtd: 1840, callsToday: 12400, success: 99.8, p50: 42, spark: series(14, 120, 40) },
  { slug: 'aws', label: 'AWS', mtd: 2210, callsToday: 8900, success: 99.9, p50: 38, spark: series(14, 150, 30) },
  { slug: 'youtube', label: 'YouTube Live API', mtd: 0, callsToday: 320, success: 98.4, p50: 210, spark: series(14, 20, 12) },
  { slug: 'cohere', label: 'Análisis inteligente (semántico)', mtd: 410, callsToday: 540, success: 99.1, p50: 880, spark: series(14, 40, 20) },
  { slug: 'gemini', label: 'Análisis inteligente (texto)', mtd: 290, callsToday: 610, success: 99.5, p50: 640, spark: series(14, 30, 15) },
  { slug: 'resend', label: 'Entrega de correo', mtd: 28, callsToday: 140, success: 100, p50: 120, spark: series(14, 10, 6) },
];

const automations = [
  { id: 'au_1', name: 'Etiquetar clips de noticias', enabled: true, trigger: 'Cuando se sube un video', runs: 312, success: 99.0, last: Date.now() - 1000 * 60 * 18 },
  { id: 'au_2', name: 'Avisar al equipo si un canal sale del aire', enabled: true, trigger: 'Cuando un canal deja de transmitir', runs: 47, success: 100, last: Date.now() - 1000 * 60 * 60 * 6 },
  { id: 'au_3', name: 'Revisar cumplimiento de cada video nuevo', enabled: false, trigger: 'Cuando un video termina de procesarse', runs: 0, success: 0, last: null },
];

const edgeNodes = range(6).map(i => ({
  id: 'node_' + (1000 + i), name: ['mx-central-1','us-east-2','eu-west-1','sa-east-1','us-west-1','mx-central-2'][i],
  provider: providers[i % providers.length], region: ['MX','US','EU','BR','US','MX'][i],
  status: ['ready','ready','ready','provisioning','ready','failed'][i],
  uptime: (99 - rnd()).toFixed(2), cpu: Math.round(20 + rnd() * 60), mem: Math.round(30 + rnd() * 50),
  lastCheck: Date.now() - rnd() * 1000 * 60 * 10,
}));

// NOTE (T301 / WIRE-0): The four pure formatters (fmtDur, fmtClock, ago,
// money) and the three label maps (statusLabels, sevLabels, roleLabels)
// moved to src/lib/format.ts on 2026-06-03. They are shared by both
// mock-backed and Convex-wired screens and must outlive this fixture.
// Import them directly from '@/lib/format' (or the relative equivalent).
const t = {
  appName: 'Circa',
};

const counts = {
  channels: channels.length,
  live: channels.filter(c => c.status === 'live' || c.status === 'adbreak').length,
  assets: assets.length,
  schedules: schedules.length,
  alerts: alerts.filter(a => !a.resolved).length,
  unread: alerts.filter(a => !a.read).length,
};

export const CIRCA = {
  member, can, channels, assets, schedules, alerts, members, providerSpend,
  automations, edgeNodes, providers, t, series, range,
  counts,
};

export type CircaMock = typeof CIRCA;
export default CIRCA;
